/**
 * Authentication service
 */
import apiClient from './api';

const authService = {
  /**
   * Login user
   * @param {string} email
   * @param {string} password
   * @returns {Promise<{token: string, user: object, tenant: object}>}
   */
  async login(email, password) {
    const response = await apiClient.post('/auth/login', { email, password });
    return response.data;
  },

  /**
   * Register new user and organization
   * @param {object} data - { email, password, full_name, company_name }
   * @returns {Promise<{token: string, user: object, tenant: object}>}
   */
  async register(data) {
    const response = await apiClient.post('/auth/register', {
      email: data.email,
      password: data.password,
      full_name: data.fullName,
      company_name: data.companyName,
    });
    return response.data;
  },

  /**
   * Get current user info
   * @returns {Promise<{user: object, tenant: object}>}
   */
  async getCurrentUser() {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  /**
   * Request password reset email
   * @param {string} email
   * @returns {Promise<{message: string}>}
   */
  async forgotPassword(email) {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  },

  /**
   * Verify if a reset token is valid
   * @param {string} token
   * @returns {Promise<{valid: boolean, message: string}>}
   */
  async verifyResetToken(token) {
    const response = await apiClient.post('/auth/verify-reset-token', { token });
    return response.data;
  },

  /**
   * Reset password with token
   * @param {string} token
   * @param {string} newPassword
   * @returns {Promise<{message: string}>}
   */
  async resetPassword(token, newPassword) {
    const response = await apiClient.post('/auth/reset-password', {
      token,
      new_password: newPassword,
    });
    return response.data;
  },

  /**
   * Verify email with token
   * @param {string} token
   * @returns {Promise<{message: string, already_verified: boolean}>}
   */
  async verifyEmail(token) {
    const response = await apiClient.post('/auth/verify-email', { token });
    return response.data;
  },

  /**
   * Resend verification email
   * @param {string} email
   * @returns {Promise<{message: string}>}
   */
  async resendVerification(email) {
    const response = await apiClient.post('/auth/resend-verification', { email });
    return response.data;
  },
};

export default authService;
